/**
 * HR Subscription Page
 * Core and Nexora plan subscription with multiple duration options for HR users
 * HR users MUST subscribe before accessing the dashboard
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Users, FileText, Brain, BarChart3, Headphones, Crown, Briefcase, X, Loader, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../hooks/useSubscription';
import RazorpayCheckout from '../../components/Payment/RazorpayCheckout';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import { firebaseApp } from '../../firebase';
import toast from 'react-hot-toast';

const db = getFirestore(firebaseApp);

type PlanDuration = '1 month' | '3 months' | '6 months' | '12 months';
type PlanType = 'core' | 'nexora';

interface DurationPricing {
  original: number;  // Original price without discount
  discounted: number;  // Discounted price
  discount: number;  // Discount percentage
}

interface PlanPricing {
  oneMonth: DurationPricing;
  threeMonths: DurationPricing;
  sixMonths: DurationPricing;
  twelveMonths: DurationPricing;
}

interface Plan {
  name: string;
  type: PlanType;
  description: string;
  pricing: PlanPricing;
  features: string[];
  highlighted: boolean;
}

const plans: Plan[] = [
  {
    name: 'Core',
    type: 'core',
    description: 'Essential features for small teams',
    pricing: {
      oneMonth: { original: 2999, discounted: 2999, discount: 0 },
      threeMonths: { original: 8997, discounted: 8547, discount: 5 },     // 5% discount
      sixMonths: { original: 17994, discounted: 17094, discount: 5 },     // 5% discount
      twelveMonths: { original: 35988, discounted: 34188, discount: 5 }, // 5% discount
    },
    features: [
      '3 Job Posts',
      '150 Student Applications',
      'AI-powered test generation',
      'Candidate screening & shortlisting',
      'Advanced proctoring features',
      'Detailed candidate analytics',
      'Standard support',
    ],
    highlighted: false,
  },
  {
    name: 'Nexora',
    type: 'nexora',
    description: 'For growing organizations',
    pricing: {
      oneMonth: { original: 7999, discounted: 7999, discount: 0 },
      threeMonths: { original: 23997, discounted: 22797, discount: 5 },   // 5% discount
      sixMonths: { original: 47994, discounted: 45594, discount: 5 },     // 5% discount
      twelveMonths: { original: 95988, discounted: 91188, discount: 5 },  // 5% discount
    },
    features: [
      '10 Job Posts',
      '500 Student Applications',
      'Advanced AI test generation',
      'Priority candidate screening',
      'Enhanced proctoring & security',
      'Advanced analytics & insights',
      'Custom branding',
      'Bulk candidate actions',
      'Email & chat support',
    ],
    highlighted: true,
  },
];

const durations: PlanDuration[] = ['1 month', '3 months', '6 months', '12 months'];

const HRSubscription: React.FC = () => {
  const { user, logout } = useAuth();
  const { subscription, loading: subscriptionLoading, hasActiveSubscription, refreshSubscription } = useSubscription();
  const navigate = useNavigate();
  const [userData, setUserData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<PlanType>('core');
  const [selectedDuration, setSelectedDuration] = useState<PlanDuration>('1 month');

  useEffect(() => {
    const fetchUserData = async () => {
      if (!user?.id) return;

      try {
        const userDoc = await getDoc(doc(db, 'recruiters', user.id));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData(data);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  const currentPlanData = plans.find(p => p.type === selectedPlan)!;

  const getPlanPricing = (plan: Plan, duration: PlanDuration): DurationPricing => {
    switch (duration) {
      case '1 month': return plan.pricing.oneMonth;
      case '3 months': return plan.pricing.threeMonths;
      case '6 months': return plan.pricing.sixMonths;
      case '12 months': return plan.pricing.twelveMonths;
      default: return plan.pricing.oneMonth;
    }
  };

  const currentPricing = getPlanPricing(currentPlanData, selectedDuration);
  const currentAmount = currentPricing.discounted;

  const handlePaymentSuccess = async () => {
    toast.success('Subscription activated successfully!');

    // Wait a moment for backend to update, then refresh subscription data
    await new Promise(resolve => setTimeout(resolve, 1000));

    await refreshSubscription();

    // Redirect to dashboard
    setTimeout(() => {
      navigate('/hr');
    }, 500);
  };

  const handlePaymentFailure = (error: any) => {
    toast.error(error.message || 'Payment failed. Please try again.');
  };

  // Check if user has valid subscription
  const hasValidSubscription =
    subscription &&
    subscription.status === 'active' &&
    subscription.plan !== 'none';

  if (loading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading subscription details...</p>
        </div>
      </div>
    );
  }

  // Show success screen if already subscribed
  if (hasValidSubscription) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              You're on {subscription?.plan?.charAt(0).toUpperCase() + subscription?.plan?.slice(1)} Plan!
            </h1>
            <p className="text-gray-600 mb-6">
              You already have an active subscription. Enjoy access to all features!
            </p>
            <button
              onClick={() => navigate('/hr')}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-purple-50">
      {/* Top Header with Logo */}
      <div className="bg-white border-b border-gray-200 py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img
              src="/vetriaAI.png"
              alt="VetriaAI"
              className="h-10 w-10 rounded-lg"
            />
            <span className="text-xl font-bold text-gray-900">VetriaAI</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              Logged in as: <span className="font-medium">{user?.email}</span>
            </div>
            <button
              onClick={() => {
                logout();
                navigate('/login');
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </div>

      <div className="py-12 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Purchase a Plan to Continue
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the perfect plan for your hiring needs. Unlock AI-powered recruitment tools.
            </p>
          </div>

        {/* Plan Selection */}
        <div className="max-w-5xl mx-auto mb-8">
          <div className="grid md:grid-cols-2 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.type}
                onClick={() => setSelectedPlan(plan.type)}
                className={`
                  relative cursor-pointer rounded-2xl p-6 transition-all border-2
                  ${selectedPlan === plan.type
                    ? 'border-indigo-600 bg-white shadow-xl'
                    : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                  }
                  ${plan.highlighted ? 'ring-2 ring-indigo-200' : ''}
                `}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                      RECOMMENDED
                    </span>
                  </div>
                )}

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      plan.type === 'core' ? 'bg-blue-100' : 'bg-purple-100'
                    }`}>
                      {plan.type === 'core' ? (
                        <Crown className={`w-6 h-6 ${plan.type === 'core' ? 'text-blue-600' : 'text-purple-600'}`} />
                      ) : (
                        <Briefcase className="w-6 h-6 text-purple-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                      <p className="text-sm text-gray-500">{plan.description}</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    selectedPlan === plan.type ? 'border-indigo-600 bg-indigo-600' : 'border-gray-300'
                  }`}>
                    {selectedPlan === plan.type && (
                      <CheckCircle className="w-4 h-4 text-white" />
                    )}
                  </div>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold text-gray-900">
                    ₹{plan.pricing.oneMonth.discounted.toLocaleString('en-IN')}
                  </span>
                  <span className="text-gray-500">/month</span>
                </div>

                <ul className="space-y-2">
                  {plan.features.slice(0, 5).map((feature, index) => (
                    <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                  {plan.features.length > 5 && (
                    <li className="text-sm text-indigo-600 font-medium">
                      +{plan.features.length - 5} more features
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Duration Selector */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center">
              Choose Duration for {currentPlanData.name} Plan
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {durations.map((duration) => {
                const pricing = getPlanPricing(currentPlanData, duration);
                const isSelected = duration === selectedDuration;
                const monthCount = duration === '1 month' ? 1 : duration === '3 months' ? 3 : duration === '6 months' ? 6 : 12;
                const perMonth = Math.round(pricing.discounted / monthCount);
                const hasDiscount = pricing.discount > 0;

                return (
                  <button
                    key={duration}
                    onClick={() => setSelectedDuration(duration)}
                    className={`
                      relative p-4 rounded-xl border-2 transition-all
                      ${isSelected
                        ? 'border-indigo-600 bg-indigo-50 shadow-md'
                        : 'border-gray-200 hover:border-indigo-300 bg-white'
                      }
                    `}
                  >
                    {hasDiscount && (
                      <div className="absolute -top-2 -right-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full">
                        {pricing.discount}% OFF
                      </div>
                    )}
                    <div className="text-center">
                      <p className="font-semibold text-gray-900">{duration}</p>
                      {hasDiscount && (
                        <p className="text-sm text-gray-400 line-through mt-1">
                          ₹{pricing.original.toLocaleString('en-IN')}
                        </p>
                      )}
                      <p className="text-2xl font-bold text-indigo-600 mt-1">
                        ₹{pricing.discounted.toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        ₹{perMonth.toLocaleString('en-IN')}/mo
                      </p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Payment Section */}
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-indigo-200">
            <div className="text-center mb-6">
              <p className="text-gray-600 mb-2">{currentPlanData.name} Plan - {selectedDuration}</p>
              {currentPricing.discount > 0 && (
                <p className="text-lg text-gray-400 line-through">
                  ₹{currentPricing.original.toLocaleString('en-IN')}
                </p>
              )}
              <div className="flex items-center justify-center gap-2">
                <span className="text-5xl font-bold text-gray-900">
                  ₹{currentAmount.toLocaleString('en-IN')}
                </span>
                {currentPricing.discount > 0 && (
                  <span className="bg-green-100 text-green-700 text-sm font-semibold px-2 py-1 rounded">
                    {currentPricing.discount}% OFF
                  </span>
                )}
              </div>
              {selectedDuration !== '1 month' && (
                <p className="text-sm text-gray-600 mt-2">
                  Total for {selectedDuration}
                </p>
              )}
            </div>

            {/* All Features */}
            <div className="mb-6 p-4 bg-gray-50 rounded-xl">
              <h4 className="font-semibold text-gray-900 mb-3">What's Included:</h4>
              <ul className="space-y-2">
                {currentPlanData.features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-sm text-gray-600">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>

            {/* Payment Component */}
            {user && userData && (
              <RazorpayCheckout
                amount={currentAmount}
                planType="HR"
                planDuration={selectedDuration}
                planName={`${currentPlanData.name} - ${selectedDuration}`}
                userDetails={{
                  name: userData.companyName || userData.name || user.email || 'User',
                  email: userData.email || user.email || '',
                  contact: userData.phone || '',
                }}
                onSuccess={handlePaymentSuccess}
                onFailure={handlePaymentFailure}
                buttonText={`Subscribe to ${currentPlanData.name} Plan`}
                buttonClassName="w-full bg-indigo-600 text-white hover:bg-indigo-700"
                notes={{ selectedPlan: selectedPlan }}
              />
            )}

          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default HRSubscription;
